import { plainToInstance, Type } from 'class-transformer'
import moment from 'moment-timezone'
import { TemplateFormAnswers } from '@core/entities/template/TemplateFormAnswers/TemplateFormAnswers'
import {
  PermitCheckInCapabilities,
  PermitOnSiteSummary,
  PermitPermittedActions,
} from './types'
import { CheckInSession } from '@core/entities/checkIn'
import { PermitTemplateRole } from '@core/entities/permit/PermitTemplateRoles'
import { Participant } from '@core/entities/participant/Participant'
import {
  CHILD_CREATING_PERMIT_STATUSES,
  PARENT_APPROVED_PERMIT_STATUSES,
  PRE_APPROVAL_PERMIT_STATUSES,
  PermitBase,
} from '@core/entities/permit/PermitBase'
import { IPermit } from '@core/entities/permit/Permit/IPermit'
import { PermitSuspension } from '@core/entities/permit/PermitSuspension'
import { User } from '@core/entities/user'
import { ApprovalStage } from '@core/entities/permit/PermitApprovalReviews'
import { PermitLastExtension } from '../PermitLastExtension'
import { SubmissionProof } from '@core/entities/template/TemplateQuestion'
import { ImageWarning } from '@core/entities/ImageWarning'
import {
  BriefingSession,
  BriefingSessionParticipant,
} from '@core/entities/briefing'
import { PermitStageDrafts } from '../PermitStageDrafts'
import { ITemplateProjectLocations } from '@core/entities/template/TemplateProjectLocations'

const END_OF_SHIFT_HOUR = 17
const FALLBACK_SHIFT_HOURS = 8

export class Permit extends PermitBase<IPermit> {
  @Type(() => TemplateFormAnswers)
  permitHolderAnswers?: TemplateFormAnswers

  @Type(() => TemplateFormAnswers)
  authorisedPersonAnswers?: TemplateFormAnswers

  @Type(() => TemplateFormAnswers)
  postPermitChecksAnswers?: TemplateFormAnswers

  @Type(() => PermitSuspension)
  permitSuspensionResult?: PermitSuspension

  @Type(() => PermitLastExtension)
  lastExtension: PermitLastExtension

  @Type(() => BriefingSession)
  lastBriefingSession?: BriefingSession

  @Type(() => User)
  approvedBy?: User

  @Type(() => User)
  rejectedBy?: User

  @Type(() => User)
  canceledBy?: User

  rejectReason?: string

  cancelReason?: string

  permittedActions: Array<PermitPermittedActions>
  permitInitiationAuthGroupsIds: Array<number>

  @Type(() => PermitBase)
  isCloneOf?: PermitBase

  @Type(() => CheckInSession)
  checkInSessions?: CheckInSession[]

  @Type(() => ImageWarning)
  warnings: Array<ImageWarning>

  @Type(() => PermitStageDrafts)
  permitStageDrafts?: Array<PermitStageDrafts>

  @Type(() => PermitBase)
  parentPermit?: PermitBase

  @Type(() => PermitBase)
  children?: Array<PermitBase>

  public static new(payload: unknown): Permit {
    const entity = plainToInstance(Permit, payload)

    return entity
  }

  public getApproverAnswers(): Array<TemplateFormAnswers> {
    return (
      this.approvalReviews?.approvals
        .filter(
          (approval) =>
            approval.action === 'APPROVED' &&
            approval.approval.approvalAnswers?.isAnswered(),
        )
        .map((approval) => approval.approval.approvalAnswers) || []
    )
  }

  public getApproverAnswersByStage(
    approvalStage: ApprovalStage,
  ): Array<TemplateFormAnswers> {
    return (
      this.approvalReviews?.approvals
        .filter(
          (approval) =>
            approval.action === 'APPROVED' &&
            approval.approval.approvalStage === approvalStage &&
            approval.approval.approvalAnswers?.isAnswered(),
        )
        .map((approval) => approval.approval.approvalAnswers) || []
    )
  }

  public hasPermission(action: PermitPermittedActions): boolean {
    const basePermission = this.permittedActions.includes(action)
    switch (action) {
      case 'REQUEST_CHANGES':
        return (
          basePermission &&
          (!this.lastRequestedChange || !!this.lastRequestedChange.addressedBy)
        )
      default:
        return basePermission
    }
  }

  public hasAnyPermissions(actions: Array<PermitPermittedActions>): boolean {
    return actions.some((action) => this.hasPermission(action))
  }

  public hasAllPermissions(actions: Array<PermitPermittedActions>): boolean {
    return actions.every((action) => this.hasPermission(action))
  }

  public hasSuspensionReason(): boolean {
    return !!this.permitSuspensionResult
  }

  public getImageS3Keys(): string[] {
    const keys: string[] = []
    if (this.permitHolderAnswers) {
      keys.push(...this.permitHolderAnswers.getImageS3Keys())
    }
    if (this.authorisedPersonAnswers) {
      keys.push(...this.authorisedPersonAnswers.getImageS3Keys())
    }
    return keys
  }

  public getProofs(): SubmissionProof[] {
    const keys: SubmissionProof[] = []
    if (this.permitHolderAnswers) {
      keys.push(...this.permitHolderAnswers.getProofs())
    }
    if (this.authorisedPersonAnswers) {
      keys.push(...this.authorisedPersonAnswers.getProofs())
    }
    return keys
  }

  public hasOngoingBriefingSession() {
    return this.lastBriefingSession?.status === 'ONGOING'
  }

  // Participants declared on the ongoing briefing session of a no-signature
  // template that haven't been promoted to real permit participants yet
  // (permit not approved). Signature templates keep unsigned participants on
  // the briefing card instead.
  public getUnpromotedBriefingParticipants(): BriefingSessionParticipant[] {
    if (this.requiresBriefingSignature()) return []
    if (!PRE_APPROVAL_PERMIT_STATUSES.includes(this.status)) return []
    if (!this.hasOngoingBriefingSession()) return []

    const realUserIds = new Set(
      this.participants.map(({ userId }) => userId).filter(Boolean),
    )

    return (this.lastBriefingSession?.getUnsignedParticipants() ?? []).filter(
      ({ userId }) => !userId || !realUserIds.has(userId),
    )
  }

  public shouldShowBriefingCard() {
    return (
      this.hasOngoingBriefingSession() &&
      this.hasPermission('BRIEF_PARTICIPANTS')
    )
  }

  public getProjectLocations(): ITemplateProjectLocations {
    if (this.projectLocations?.locations?.length > 0) {
      return this.projectLocations.toPlain()
    }

    if (this.location) {
      return {
        locations: [
          {
            locationName: this.location,
            locationNodeId: '',
          },
        ],
      }
    }

    return { locations: [] }
  }

  public isCheckInEnabled(): boolean {
    return this.template?.currentVersion?.enableCheckIn === true
  }

  public getDefaultCheckOutOn(now = moment()): string {
    const timezone = this.project?.timezone
    const localNow = timezone ? now.clone().tz(timezone) : now.clone()
    const endsOn = timezone
      ? moment(this.endsOn).tz(timezone)
      : moment(this.endsOn)

    if (endsOn.isSame(localNow, 'day') && endsOn.isAfter(localNow)) {
      return endsOn.toISOString()
    }
    const endOfShift = localNow.clone().hour(END_OF_SHIFT_HOUR).startOf('hour')
    if (endOfShift.isAfter(localNow)) {
      return endOfShift.toISOString()
    }
    return localNow.clone().add(FALLBACK_SHIFT_HOURS, 'hours').toISOString()
  }

  public getCheckInSessions(): CheckInSession[] {
    if (!this.isCheckInEnabled()) return []
    return this.checkInSessions ?? []
  }

  public getOpenCheckInSessions(): CheckInSession[] {
    return this.getCheckInSessions().filter((session) => session.isOpen())
  }

  public getCheckInSessionForUser(userId?: number): CheckInSession | undefined {
    if (!userId) return undefined
    return this.getCheckInSessions().find(({ user }) => user?.id === userId)
  }

  public isUserCheckedIn(userId?: number): boolean {
    return !!this.getCheckInSessionForUser(userId)?.isOpen()
  }

  public getPeopleUserIds(): number[] {
    return Array.from(
      new Set<number>(
        [
          this.permitHolder?.id,
          this.permitAuthor?.id,
          ...this.participants
            .filter(({ status }) => status === 'ACTIVE')
            .map(({ userId }) => userId),
          ...this.getCheckInSessions().map(({ user }) => user?.id),
        ].filter(Boolean),
      ),
    )
  }

  public getOnSiteSummary(): PermitOnSiteSummary {
    const open = this.getOpenCheckInSessions()
    return {
      onSite: open.length,
      overdue: open.filter((session) => session.isOverdue()).length,
      total: this.getPeopleUserIds().length,
    }
  }

  public getUnlistedOpenCheckInSessions(): CheckInSession[] {
    const listed = new Set<number>(
      [
        this.permitHolder?.id,
        this.permitAuthor?.id,
        ...this.participants.map(({ userId }) => userId),
        ...this.getReviews().map(({ submittedBy }) => submittedBy?.id),
      ].filter(Boolean),
    )
    return this.getOpenCheckInSessions().filter(
      ({ user }) => !!user && !listed.has(user.id),
    )
  }

  public isPermitHolderOrAuthor(userId?: number): boolean {
    return (
      !!userId &&
      (this.permitHolder?.id === userId || this.permitAuthor?.id === userId)
    )
  }

  public isApprovedBy(userId?: number): boolean {
    return (
      !!userId &&
      this.getReviews('APPROVED').some(
        ({ submittedBy }) => submittedBy?.id === userId,
      )
    )
  }

  public getCheckInCapabilities(
    userId?: number,
    templateRoles: PermitTemplateRole[] = [],
  ): PermitCheckInCapabilities {
    const none: PermitCheckInCapabilities = {
      canCheckSelfIn: false,
      canCheckSelfOut: false,
      canExtendSelf: false,
      canCheckOthersIn: false,
      canCheckOthersOut: false,
      canForceCheckOut: false,
    }
    if (!userId || !this.isCheckInEnabled()) return none

    const me = this.participants.find(
      (participant) =>
        participant.userId === userId && participant.status === 'ACTIVE',
    )
    const acceptedRoleIds = new Set(
      (me?.roles ?? [])
        .filter(
          ({ briefingAcceptanceStatus }) =>
            briefingAcceptanceStatus !== 'PENDING_ACCEPTANCE' &&
            briefingAcceptanceStatus !== 'DECLINED',
        )
        .map(({ roleId }) => roleId),
    )
    const myRoles = templateRoles.filter(({ id }) => acceptedRoleIds.has(id))

    const canCheckIn = this.hasPermission('CHECK_IN')
    const canCheckOut = this.hasPermission('CHECK_OUT')
    const controlsOthers = this.hasPermission('CHECK_OTHERS_IN_OUT')
    const canCheckOthersIn = canCheckIn && controlsOthers
    // CHECK_IN is granted when the user can check in themselves or others, so
    // only hide self check-in from users whose roles control others but don't
    // allow self.
    const selfRoute =
      this.isPermitHolderOrAuthor(userId) ||
      this.isApprovedBy(userId) ||
      myRoles.some((role) => role.canCheckSelfIn()) ||
      !myRoles.some((role) => role.canCheckOthersIn())
    const checkedIn = this.isUserCheckedIn(userId)

    return {
      canCheckSelfIn: canCheckIn && selfRoute && !checkedIn,
      canCheckSelfOut: canCheckOut && checkedIn,
      canExtendSelf: canCheckIn && checkedIn,
      canCheckOthersIn,
      canCheckOthersOut: canCheckOut && controlsOthers,
      canForceCheckOut: this.hasPermission('FORCE_CHECK_OUT'),
    }
  }

  public getOpenCheckInSessionsForOthers(userId?: number): CheckInSession[] {
    return this.getOpenCheckInSessions().filter(
      ({ user }) => !!user && user.id !== userId,
    )
  }

  public getParticipantsEligibleForCheckIn(
    excludeUserId?: number,
  ): Participant[] {
    return this.participants.filter(
      (participant) =>
        participant.status === 'ACTIVE' &&
        participant.userId !== excludeUserId &&
        !!participant.userId &&
        !!participant.roles?.length &&
        !participant.hasPendingAcceptance() &&
        !this.isUserCheckedIn(participant.userId),
    )
  }

  public isChildPermit(): boolean {
    return !!this.parentPermit
  }

  public isParentApprovedOrLater(): boolean {
    return (
      !!this.parentPermit &&
      PARENT_APPROVED_PERMIT_STATUSES.includes(this.parentPermit.status)
    )
  }

  public canRaiseChildBasedOnStatus(): boolean {
    return CHILD_CREATING_PERMIT_STATUSES.includes(this.status)
  }

  public getLinkedPermitCount(): number {
    return (this.parentPermit ? 1 : 0) + (this.children?.length ?? 0)
  }
}
